import { baseApi } from '@/store/api/baseApi';
import type {
  AdminUser,
  AdminUserListParams,
  BulkAdminUserIdsPayload,
  BulkAdminUserStatusPayload,
  CreateAdminUserPayload,
  CreateAccessProfilePayload,
  PaginatedAdminUsers,
  PermissionsConfig,
  ResetAdminUserPasswordPayload,
  UpdateAdminUserPayload,
  UpdateAccessProfilePayload,
  UpdateRolePermissionsPayload,
  AccessProfile,
} from '@/types/adminUser';

function buildUserListQuery(params: AdminUserListParams): string {
  const searchParams = new URLSearchParams();
  searchParams.set('page', String(params.page ?? 1));
  searchParams.set('limit', String(params.limit ?? 10));
  searchParams.set('sortBy', params.sortBy ?? 'createdAt');
  searchParams.set('sortOrder', params.sortOrder ?? 'desc');

  if (params.search?.trim()) searchParams.set('search', params.search.trim());
  if (params.status) searchParams.set('status', params.status);
  if (params.role) searchParams.set('role', params.role);

  return searchParams.toString();
}

export const usersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAdminUsers: builder.query<PaginatedAdminUsers, AdminUserListParams>({
      query: (params) => ({
        url: `/users?${buildUserListQuery(params)}`,
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}(${buildUserListQuery(queryArgs)})`,
      forceRefetch: ({ currentArg, previousArg }) =>
        buildUserListQuery(currentArg ?? {}) !== buildUserListQuery(previousArg ?? {}),
      providesTags: (result) =>
        result?.results
          ? [
              ...result.results.map(({ id }) => ({ type: 'Users' as const, id })),
              { type: 'Users', id: 'LIST' },
            ]
          : [{ type: 'Users', id: 'LIST' }],
    }),
    getAdminUser: builder.query<AdminUser, string>({
      query: (id) => `/users/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Users', id }],
    }),
    createAdminUser: builder.mutation<AdminUser, CreateAdminUserPayload>({
      query: (body) => ({ url: '/users/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'Users', id: 'LIST' }],
    }),
    updateAdminUser: builder.mutation<AdminUser, { id: string; body: UpdateAdminUserPayload }>({
      query: ({ id, body }) => ({ url: `/users/update/${id}`, method: 'POST', body }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Users', id },
        { type: 'Users', id: 'LIST' },
        'Auth',
      ],
    }),
    deleteAdminUser: builder.mutation<void, string>({
      query: (id) => ({ url: `/users/delete/${id}`, method: 'POST' }),
      invalidatesTags: [{ type: 'Users', id: 'LIST' }],
    }),
    bulkUpdateAdminUserStatus: builder.mutation<{ count: number }, BulkAdminUserStatusPayload>({
      query: (body) => ({ url: '/users/bulk-status', method: 'POST', body }),
      invalidatesTags: [{ type: 'Users', id: 'LIST' }],
    }),
    bulkDeleteAdminUsers: builder.mutation<{ count: number }, BulkAdminUserIdsPayload>({
      query: (body) => ({ url: '/users/bulk-delete', method: 'POST', body }),
      invalidatesTags: [{ type: 'Users', id: 'LIST' }],
    }),
    resetAdminUserPassword: builder.mutation<void, { id: string; body: ResetAdminUserPasswordPayload }>({
      query: ({ id, body }) => ({ url: `/users/reset-password/${id}`, method: 'POST', body }),
    }),
    getPermissionsConfig: builder.query<PermissionsConfig, void>({
      query: () => '/users/permissions/config',
      providesTags: [{ type: 'Users', id: 'PERMISSIONS' }],
    }),
    updateRolePermissions: builder.mutation<
      { role: string; modules: string[]; rolePermissions: Record<string, string[]> },
      UpdateRolePermissionsPayload
    >({
      query: (body) => ({ url: '/users/permissions/roles', method: 'POST', body }),
      invalidatesTags: [
        { type: 'Users', id: 'PERMISSIONS' },
        { type: 'Users', id: 'LIST' },
        { type: 'Auth' },
      ],
    }),
    createAccessProfile: builder.mutation<AccessProfile, CreateAccessProfilePayload>({
      query: (body) => ({ url: '/users/permissions/profiles/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'Users', id: 'PERMISSIONS' }, 'Auth'],
    }),
    updateAccessProfile: builder.mutation<AccessProfile, { id: string; body: UpdateAccessProfilePayload }>({
      query: ({ id, body }) => ({
        url: `/users/permissions/profiles/update/${id}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'Users', id: 'PERMISSIONS' },
        { type: 'Users', id: 'LIST' },
        'Auth',
      ],
    }),
    deleteAccessProfile: builder.mutation<void, string>({
      query: (id) => ({ url: `/users/permissions/profiles/delete/${id}`, method: 'POST' }),
      invalidatesTags: [
        { type: 'Users', id: 'PERMISSIONS' },
        { type: 'Users', id: 'LIST' },
        'Auth',
      ],
    }),
  }),
});

export const {
  useGetAdminUsersQuery,
  useGetAdminUserQuery,
  useCreateAdminUserMutation,
  useUpdateAdminUserMutation,
  useDeleteAdminUserMutation,
  useBulkUpdateAdminUserStatusMutation,
  useBulkDeleteAdminUsersMutation,
  useResetAdminUserPasswordMutation,
  useGetPermissionsConfigQuery,
  useUpdateRolePermissionsMutation,
  useCreateAccessProfileMutation,
  useUpdateAccessProfileMutation,
  useDeleteAccessProfileMutation,
} = usersApi;
